import '../scss/GroupView.scss';
import React, {useEffect, useRef, useState} from "react";
import {useCurrentUserData} from "../App";
import {Link, Navigate, useLocation, useNavigate, useParams} from "react-router-dom";
import {GetCodes, GetContacts, GetSurveyWithId, GetUserGroupData, PostSupportNeedUpdate} from "../API";
import {formatDate, getStudentFromContacts, getTopicDisplayName, getTimes, transformGroupData} from "../formats";
import {FormattedMessage, useIntl} from "react-intl";
import {ReactComponent as Chevron} from "../svg/chevron.svg";
import {ReactComponent as SeenIcon} from "../svg/success.svg";
import {ReactComponent as LoadingIcon} from "../svg/loading.svg";
import {useSnackbar} from "../Content";
import {Skeleton} from "../UIElements";
import {ReactComponent as Face} from "../svg/face.svg";

export function GroupView() {
    const userGroupData = GetUserGroupData()
    const getContacts = GetContacts()
    const getCodes = GetCodes()

    return <div className={"group-view"}>
        {userGroupData.isSuccess && getContacts.isSuccess && getCodes.isSuccess &&
            <GroupViewContent data={userGroupData?.data} contacts={getContacts?.data} codes={getCodes.data[0]} refetchData={()=>userGroupData?.refetch()}/>
        }
        {userGroupData.isLoading || getContacts.isLoading || getCodes.isLoading &&
            <GroupViewSkeleton />
        }
        {userGroupData.isError || getContacts.isError || getCodes.isError &&
            <h1>Error in loading data for group view</h1>
        }
        </div>
}

function GroupViewContent({data, contacts, codes, refetchData}) {
    const combinedData = transformGroupData(data)
    const dataSortedByEndTime = combinedData.sort((a, b) => b?.survey?.endtime.localeCompare(a?.survey?.endtime))
    const surveys = dataSortedByEndTime
    const { surveyId } = useParams()
    const endedLessThanAMonthAgo = endTime => {
        const formattedEndTime = new Date(formatDate(endTime))
        const dateDifference = new Date() - formattedEndTime
        const time = getTimes(dateDifference)
        return time.year === 0 && time.month <= 1
    }
    const recentSurveys = surveys.filter(data => endedLessThanAMonthAgo(data?.survey?.starttime) )
    const olderSurveys = surveys.filter(data => !endedLessThanAMonthAgo(data?.survey?.starttime) )
    const activeSurveyData = surveyId ? combinedData.filter(data => data?.survey?.id.includes(surveyId)) : dataSortedByEndTime
    const activeSurveyInOlderSurveys = olderSurveys.some(survey => survey?.survey?.id.includes(surveyId))
    const activeSurveySupportNeeds = activeSurveyData[0]?.teachersupport?.length > 0 && activeSurveyData[0]?.teachersupport.filter(supportNeed => supportNeed?.status === "OPENED" || supportNeed?.status === "NEW")
    const surveyName = surveyId ? activeSurveyData[0]?.survey?.title : "default survey"
    const surveyTitle = surveyName?.length > 20 ? surveyName.substring(0, 20) + "..." : surveyName
    useEffect(() => {
        if (surveyId) {
            document.title = surveyTitle + " | Annie Advisor"
        }
    }, [surveyId])

    const surveyHasMatchingSurvey = (id) => {
        return combinedData.some(survey => survey?.survey?.id.includes(id))
    }
    const surveyIdHasMatchingSurvey = surveyHasMatchingSurvey(surveyId)
    let navigate = useNavigate()
    const mostRecentSurveyId = sessionStorage.getItem("previousSurveyId") && surveyHasMatchingSurvey(sessionStorage.getItem("previousSurveyId")) ? sessionStorage.getItem("previousSurveyId") : dataSortedByEndTime[0]?.survey?.id[0]
    useEffect(()=>{
        if (!surveyId || !surveyIdHasMatchingSurvey) {
            if (mostRecentSurveyId) {
                navigate("/group/"+mostRecentSurveyId, { replace: true })
            }
        } else {
            sessionStorage.setItem("previousSurveyId", surveyId)
        }
    },[])

    return <>
        {surveys.length > 0 ?
            <>
                <div className={"tabs-container"}>
                <div className={"survey-tabs"}>
                    {recentSurveys.map((data, i) => {
                        const survey = data?.survey
                        const supportNeeds = data?.teachersupport?.length > 0 && data?.teachersupport.filter(supportNeed => supportNeed?.status === "OPENED" || supportNeed?.status === "NEW")
                        return <Link to={survey?.id[0]} key={i} className={survey?.id.includes(surveyId) ? "tab active" : "tab"} title={survey?.title}>
                            {survey?.title}
                            {supportNeeds?.length > 0 &&
                            <span className={"highlight-number"}>{supportNeeds?.length}</span>
                            }
                        </Link>
                    })}
                    {activeSurveyInOlderSurveys &&
                    <div className={"tab active"}>
                        {activeSurveyData[0]?.survey?.title}
                        {activeSurveySupportNeeds?.length > 0 &&
                        <span className={"highlight-number"}>{activeSurveySupportNeeds?.length}</span>
                        }
                    </div>
                    }
                    {olderSurveys.length > 0 &&
                        <OlderSurveysSelector surveys={activeSurveyInOlderSurveys ? olderSurveys.filter(survey => !survey?.survey?.id.includes(surveyId)) : olderSurveys} activeSurvey={surveyId} />
                    }
                </div>
                </div>
                {surveyIdHasMatchingSurvey &&
                <ActiveSurveyGroupView data={activeSurveyData[0]} contacts={contacts} codes={codes} refetchData={refetchData}/>
                }
            </>
            :
            <div className={"no-requests-container"}>
                <div id={"no-requests"}>
                    <Face />
                </div>
                <h4>
                    <FormattedMessage
                        id={"campaignsNotStarted"}
                        defaultMessage={"You don't have any active campaigns.{br}We'll send an email once a campaign has started."}
                        values={{br: <br/>}}/>
                </h4>
            </div>
        }</>
}

function ActiveSurveyGroupView({data, contacts, codes, refetchData}) {
    const intl = useIntl()
    const teacherSupportNeeds = data?.teachersupport
    const noResponse = data?.group?.noresponse
    const notFinishedResponding = data?.group?.startednotfinished
    const notDelivered = data?.group?.notdelivered
    const everythingOk = data?.group?.everythingok
    const otherSupport = data?.group?.othersupport
    const recipientsAmount = teacherSupportNeeds?.length + noResponse?.length + notFinishedResponding?.length + notDelivered?.length + everythingOk?.length + otherSupport?.length
    const surveyStartDate = data?.survey?.starttime ? new Date(formatDate(data?.survey?.starttime)).toLocaleString(intl.locale, {year: 'numeric', month: 'numeric', day: 'numeric'}) : ""
    const surveyEndDate = data?.survey?.endtime ? new Date(formatDate(data?.survey?.endtime)).toLocaleString(intl.locale, {year: 'numeric', month: 'numeric', day: 'numeric'}) :""

    return <>
        <div className={"survey-info"}>
            <p>
                <b>{intl.formatMessage({
                    id: "recipients",
                    defaultMessage: "Recipients in your group"
                })}:</b> {recipientsAmount}
            </p>
            <p>
                <b>
                    {intl.formatMessage({
                        id: "surveyDate",
                        defaultMessage: "Date of Survey"
                    })}:</b> {surveyStartDate} – {surveyEndDate}
            </p>
        </div>
        <div className={"survey-groups"}>
            <SurveyGroup
                header={intl.formatMessage({
                    id: "wantsYourSupport",
                    defaultMessage: "Requested your support"
                })}
                contacts={teacherSupportNeeds}
                contactsData={contacts}
                supportNeeds={true}
                codes={codes}
                refetchData={refetchData}
                />
            <SurveyGroup
                header={intl.formatMessage({
                    id: "unanswered",
                    defaultMessage: "Hasn't answered"
                })}
                contacts={noResponse}
                contactsData={contacts}
            />
            <SurveyGroup
                header={intl.formatMessage({
                    id: "notFinishedResponding",
                    defaultMessage: "Not finished answering"
                })}
                contacts={notFinishedResponding}
                contactsData={contacts}
            />
            <SurveyGroup
                header={intl.formatMessage({
                    id: "deliveryFailed",
                    defaultMessage: "Message delivery failed"
                })}
                contacts={notDelivered}
                contactsData={contacts}
            />
            <SurveyGroup
                header={intl.formatMessage({
                    id: "everythingOk",
                    defaultMessage: "Everything OK"
                })}
                contacts={everythingOk}
                contactsData={contacts}
            />
            <SurveyGroup
                header={intl.formatMessage({
                    id: "requestedOtherSupport",
                    defaultMessage: "Requested other support"
                })}
                contacts={otherSupport}
                contactsData={contacts}
            />
        </div>
    </>
}

function SurveyGroup({header, contacts, contactsData, supportNeeds, codes, refetchData}) {
    const intl = useIntl()
    const amount = contacts?.length
    let groupClass = "group"
    if (supportNeeds) {
        groupClass = groupClass.concat(" ", "needs-support")
    }
    if (amount < 1) {
        groupClass = groupClass.concat(" ", "disabled")
    }
    const statusIsNewOrOpened = status => {
        if (!status) {return false}
        return status === "NEW" || status === "OPENED"
    }
    const newOrOpenedStatus = supportNeeds && contacts.filter(contact => statusIsNewOrOpened(contact?.status))
    const otherThanOpen = supportNeeds && contacts.filter(contact => !statusIsNewOrOpened(contact?.status))
    const [showAllContacts, setShowAllContacts] = useState(false)
    const toggledContacts = showAllContacts ? contacts : contacts.slice(0, 5)
    const otherThanOpenAmount = otherThanOpen?.length
    const toggledOtherThanOpen = !supportNeeds ? contacts : showAllContacts ? otherThanOpen : otherThanOpen.slice(0,5)

    const [loading, setLoading] = useState(false)
    const {setSnackbarData} = useSnackbar()
    const postSupportNeed = PostSupportNeedUpdate()
    const markRequestSeen = (obj, resolve, reject) => {
        const postData = {
            "contact": obj?.contact,
            "survey": obj?.survey,
            "category": obj?.category,
            "status": "ACKED",
            "supporttype": obj?.supporttype,
            "followuptype": obj?.followuptype,
            "followupresult": obj?.followupresult,
            "followupstatus": obj?.followupstatus
        }
        postSupportNeed.mutate(postData, {
            onError: error => {
                console.log(error)
                reject(error)
            },
            onSuccess:()=>{
                resolve()
            }
        })
    }
    const markAllRequestsSeen = () => {
        setLoading(true)
        const postPromise = newOrOpenedStatus.reduce((postChain, obj) => {
            return postChain.then(()=> new Promise( (resolve, reject) => {
                markRequestSeen(obj, resolve, reject)
            }))
        }, Promise.resolve())

        postPromise.then(()=>{
            setLoading(false)
            setSnackbarData({
                show: true,
                text: intl.formatMessage({id:"requestsMarkedSeen",defaultMessage:"Requests marked seen!"}),
                state: "success",
                autoHide: true,
            })
            refetchData()
        }).catch(()=>{
            setLoading(false)
            setSnackbarData({
                show: true,
                text: intl.formatMessage({id:"markingSeenFailed",defaultMessage:"Marking requests failed. Please try again later."}),
                state: "error",
                autoHide: true,
            })
            refetchData()
        })
    }

    return <div className={groupClass}>
        <div className={"header"}>
            <h2>{header} ({amount})</h2>
            {supportNeeds && newOrOpenedStatus?.length > 0 && (
                    loading ?
                <button className={"loading"} disabled>
                    <LoadingIcon />
                    {intl.formatMessage({
                        id: 'markingAsSeen',
                        defaultMessage: 'Marking...',
                    })}
                </button> :
            <button onClick={() => markAllRequestsSeen()}>
                <SeenIcon/>
                {intl.formatMessage({
                    id: "markAsSeen",
                    defaultMessage: "Mark as seen"
                })}
            </button>
            )}
            {supportNeeds && newOrOpenedStatus?.length < 1 &&
                <button disabled>
                    <SeenIcon />
                    {intl.formatMessage({
                        id: "allSeen",
                        defaultMessage:"All seen"
                    })}
                </button>
            }
        </div>
        <div className={"body"}>
            {supportNeeds ?
            <table>
                <thead>
                <tr>
                    <th>
                        {intl.formatMessage({
                            id:"studentName",
                            defaultMessage:"Student"
                        })}
                    </th>
                    <th>
                        {intl.formatMessage({
                            id:"supportTopic",
                            defaultMessage:"Topic"
                        })}
                    </th>
                    <th>
                        {intl.formatMessage({
                            id:"followup",
                            defaultMessage:"Follow up"
                        })}
                    </th>
                </tr>
                </thead>
                <tbody>
                {amount > 0 ?
                    <>
                    {newOrOpenedStatus?.length > 0 &&
                    <LinksToRequests contacts={newOrOpenedStatus} contactsData={contactsData} codes={codes}/>
                    }
                    {otherThanOpen?.length > 0 &&
                        <>
                            {newOrOpenedStatus?.length > 0 &&
                            <tr className={"no-hover row-header"}><td>{intl.formatMessage({id:"markedSeen", defaultMessage:"Marked seen"})}</td><td/><td/></tr>
                            }
                            <LinksToRequests contacts={toggledOtherThanOpen} contactsData={contactsData} codes={codes}/>
                            {otherThanOpenAmount > 5 &&
                            <tr className={"no-hover"}>
                                <td>
                                    <button className={"text-button show-all"} onClick={()=>setShowAllContacts(!showAllContacts)}>
                                        {showAllContacts ?
                                            intl.formatMessage({id:"hideSome", defaultMessage:"Hide some"}) :
                                            intl.formatMessage({id:"showAll", defaultMessage:"Show all"})
                                        }
                                    </button>
                                </td><td/><td/>
                            </tr>
                            }
                        </>
                    }
                    </>
                 :
                    <tr>
                    <td className={"no-contacts"}>
                        {intl.formatMessage({id: "noStudents", defaultMessage: "No students"})}
                    </td>
                    </tr>
                }
                </tbody>
            </table> :
                amount > 0 ?
                    amount < 6 ?
                        contacts.map((contact, i) => {
                            const contactId = supportNeeds ? contact?.contact : contact
                            const student = getStudentFromContacts(contactId, contactsData)
                            const studentName = student?.meta?.firstname + " " + student?.meta?.lastname
                            return <p key={i}>{studentName}</p>
                        }) :
                        <>
                            {toggledContacts.map((contact, i) => {
                                const contactId = supportNeeds ? contact?.contact : contact
                                const student = getStudentFromContacts(contactId, contactsData)
                                const studentName = student?.meta?.firstname + " " + student?.meta?.lastname
                                return <p key={i}>{studentName}</p>
                            })}
                            <button className={"text-button show-all"} onClick={()=>setShowAllContacts(!showAllContacts)}>
                                {showAllContacts ?
                                    intl.formatMessage({id:"hideSome", defaultMessage:"Hide some"}) :
                                    intl.formatMessage({id:"showAll", defaultMessage:"Show all"})
                                }
                            </button>
                        </>
                    :
                    <p className={"no-contacts"}>
                        {intl.formatMessage({id: "noStudents", defaultMessage: "No students"})}
                    </p>
            }
        </div>
    </div>
}

function LinksToRequests({contacts, contactsData, codes}) {
    const intl = useIntl()
    let navigate = useNavigate()
    const location = useLocation()
    const followupResults = ["NORESPONSE","GOTHELP","NOHELP","OTHER","INPROGRESS"]

    return <>
        {contacts.map((contact,i)=> {
            const contactId = contact?.contact
            const student = getStudentFromContacts(contactId, contactsData)
            const studentName = student?.meta?.firstname + " " + student?.meta?.lastname
            const requestId = contact?.id.toString()
            const supportTopic = codes.hasOwnProperty(contact?.category) ? getTopicDisplayName(contact?.category, codes[contact?.category], intl) : intl.formatMessage({id:"missingNameForCategory",defaultMessage:"Name missing for support category"})
            const followUpType = contact?.followuptype
            const noFollowup = !followUpType || followUpType === "NOFOLLOWUP"
            const followUpStarted = contact?.followupstatus
            const followUpFinished = contact?.followupstatus === "FINISHED"
            const followupResult = !followUpFinished ? intl.formatMessage({id:"followUpInProgress",defaultMessage:"In progress"}) :
                followupResults.includes(contact?.followupresult) ? intl.formatMessage({id:contact?.followupresult, defaultMessage:contact?.followupresult})
                : contact?.followupresult.toString()
            const followUpText = noFollowup ? intl.formatMessage({id:"noFollowup", defaultMessage:"Ei seurantaa"}) :
                followUpStarted ? followupResult :
                    intl.formatMessage({id:"scheduledTime", defaultMessage:"Scheduled"})
            const navigateToRequest = () => {
                navigate("/request/"+requestId, {state:{direction:'left', animation: "true", from: location}})
            }
            const newRequest = contact?.status === "NEW" || contact?.status === "OPENED"

            return <tr className={newRequest ? "new" : ""} key={i} onClick={()=>navigateToRequest()}>
                <td className={"emphasis"}>{studentName}</td>
                <td>{supportTopic}</td>
                <td className={followUpStarted ? "" : "italic"}>{followUpText}</td>
            </tr>})}
    </>
}

function OlderSurveysSelector({surveys, activeSurvey}) {
    const [showMenu, setShowMenu] = useState(false)
    const popoverRef = useRef(null)
    const popoverTriggerRef = useRef(null)
    const intl = useIntl()
    const newSupportNeedsCount = surveys.reduce((count, survey) => {
        return count + (survey?.teachersupport || []).reduce((innerCount, supportNeed) => {
            if (supportNeed?.status === "OPENED" || supportNeed?.status === "NEW") {
                return innerCount + 1
            }
            return innerCount
        }, 0)
    }, 0)

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !popoverTriggerRef.current.contains(event.target) ) {
                setShowMenu(false)
            }
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            }
        }
    },[popoverRef])

    if (surveys?.length < 1) {
        return null
    }

    return <div className={"older-surveys-container"}>
        <button onClick={()=>setShowMenu((!showMenu))} ref={popoverTriggerRef}>
            <Chevron/> {intl.formatMessage({
            id: "olderSurveys",
            defaultMessage: "Older surveys"
        })}
            {newSupportNeedsCount > 0 &&
                <span className={"highlight-number"}>{newSupportNeedsCount}</span>
            }
        </button>
        {showMenu &&
        <div className={"older-surveys"} ref={popoverRef}>
            {surveys.map((data,i) => {
                const survey = data?.survey
                const supportNeeds = data?.teachersupport?.length > 0 && data?.teachersupport.filter(supportNeed => supportNeed?.status === "OPENED" || supportNeed?.status === "NEW")
                return <Link to={survey?.id[0]} key={i} className={survey?.id.includes(activeSurvey) ? "active" : ""}>
                    {survey?.title}
                    {supportNeeds?.length > 0 &&
                    <span className={"highlight-number"}>{supportNeeds?.length}</span>
                    }
                </Link>
            })}
        </div>
        }
    </div>
}

function GroupViewSkeleton() {
    const intl = useIntl()
    return <>
        <div className={"tabs-container"}>
            <div className={"survey-tabs"}>
                <div className={"tab active"}>
                    {intl.formatMessage({id:"loadingCampaigns", defaultMessage:"Loading campaigns..."})}
                </div>
            </div>
        </div>
        <div className={"survey-info"}>
            <Skeleton width={250} height={14} marginBotom={12}/>
            <Skeleton width={250} height={14} marginBotom={12}/>
        </div>
        <div className={"survey-groups skeleton-container"}>
            <div className={"group needs-support"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
            <div className={"group"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
            <div className={"group"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
            <div className={"group"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
            <div className={"group"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
            <div className={"group"}>
                <div className={"header"}>
                    <h2>
                        <Skeleton width={200} height={17} marginBotom={0}/>
                    </h2>
                </div>
                <div className={"body"}>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                    <Skeleton width={150} height={17} marginBotom={10}/>
                </div>
            </div>
        </div>
    </>
}